const getDirection = (sort) => {
  if (sort?.direction) return String(sort.direction).toLowerCase() === 'desc' ? 'desc' : 'asc';
  return sort?.desc ? 'desc' : 'asc';
};

const getField = (sort) => sort?.field || sort?.id || '';

const normalizeSort = (sort) => {
  const field = getField(sort);
  if (!field) return null;

  return {
    field,
    direction: getDirection(sort)
  };
};

const hasIdSort = (sorting) => sorting.some((sort) => sort.field === 'id' || sort.field === '_id');

// `references` is resolved client-side only (see sortKnowledgeBaseRows below): it isn't a
// plain sortable column on the backend, so forwarding it as order_by makes the API 500.
const BACKEND_UNSUPPORTED_SORT_FIELDS = ['references'];

export const sanitizeBackendOrderBy = (orderBy) => {
  if (!Array.isArray(orderBy)) return orderBy;
  const firstField = normalizeSort(orderBy[0])?.field;
  if (BACKEND_UNSUPPORTED_SORT_FIELDS.includes(firstField)) return [];

  return orderBy;
};

export const normalizeKnowledgeBaseSorting = (sorting) => {
  const normalizedSorting = (Array.isArray(sorting) ? sorting : [])
    .map(normalizeSort)
    .filter(Boolean);

  const firstSort = normalizedSorting[0];
  if (!firstSort || hasIdSort(normalizedSorting)) return normalizedSorting;

  if (firstSort.field === 'create_date' || firstSort.field === 'references') {
    return [
      ...normalizedSorting,
      {
        field: 'id',
        direction: firstSort.direction
      }
    ];
  }

  return normalizedSorting;
};

const getTemplateId = (template) => {
  const id = template?.id ?? template?._id ?? '';
  const numericId = Number(id);
  return Number.isNaN(numericId) ? String(id) : numericId;
};

const compareId = (templateA, templateB, direction) => {
  const idA = getTemplateId(templateA);
  const idB = getTemplateId(templateB);
  let result = 0;

  if (typeof idA === 'number' && typeof idB === 'number') {
    result = idA - idB;
  } else {
    result = String(idA).localeCompare(String(idB));
  }

  return direction === 'desc' ? -result : result;
};

const getReferencesValue = (template) => {
  const references = template?.references ?? template?.refs ?? '';
  const value = Array.isArray(references) ? references.filter(Boolean).join(' ') : references;
  return String(value || '').trim().toLowerCase();
};

const compareReferences = (templateA, templateB, direction) => {
  const referencesA = getReferencesValue(templateA);
  const referencesB = getReferencesValue(templateB);
  const isEmptyA = referencesA === '';
  const isEmptyB = referencesB === '';

  if (isEmptyA && isEmptyB) return compareId(templateA, templateB, direction);
  if (isEmptyA || isEmptyB) {
    if (direction === 'asc') return isEmptyA ? -1 : 1;
    return isEmptyA ? 1 : -1;
  }

  const result = referencesA.localeCompare(referencesB);
  if (result === 0) return compareId(templateA, templateB, direction);
  return direction === 'desc' ? -result : result;
};

const getCreatedDateValue = (template) => {
  const value = template?.create_at || template?.create_date || '';
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const compareCreatedDate = (templateA, templateB, direction) => {
  const dateA = getCreatedDateValue(templateA);
  const dateB = getCreatedDateValue(templateB);
  const result = dateA - dateB;

  if (result === 0) return compareId(templateA, templateB, direction);
  return direction === 'desc' ? -result : result;
};

export const sortKnowledgeBaseRows = (rows, orderBy) => {
  const firstSort = Array.isArray(orderBy) ? orderBy[0] : null;
  if (!firstSort || !Array.isArray(rows)) return rows;

  const { field, direction } = normalizeSort(firstSort) || {};
  if (field === 'references') return [...rows].sort((a, b) => compareReferences(a, b, direction));
  if (field === 'create_date') return [...rows].sort((a, b) => compareCreatedDate(a, b, direction));

  return rows;
};
